import React, { useState, useMemo, useRef, useEffect } from "react";

export interface ListColumn<T> {
  key: keyof T | string;
  label: string;
  render?: (item: T) => React.ReactNode;
  className?: string;
  sortable?: boolean;
  width?: string; // e.g., "col-span-2", "col-span-3"
}

export interface ListAction<T> {
  label: string;
  icon: React.ReactNode;
  onClick: (item: T) => void;
  disabled?: (item: T) => boolean;
  /** When set, the action is only shown when this returns true. */
  visible?: (item: T) => boolean;
  className?: string;
  title?: string;
}

export interface DataListProps<T> {
  data: T[];
  columns: ListColumn<T>[];
  actions?: ListAction<T>[];
  keyExtractor: (item: T) => string | number;
  containerClassName?: string;
  headerClassName?: string;
  rowClassName?: string | ((item: T) => string);
  emptyMessage?: string;
  loading?: boolean;
  onRowClick?: (item: T) => void;
  /** When true, shows a search input to filter rows (client-side). */
  showFilter?: boolean;
  /** Placeholder for the filter input. */
  filterPlaceholder?: string;
  /** Custom function to get searchable text from an item. If not set, uses column keys. */
  getSearchableText?: (item: T) => string;
  /**
   * When > 0, enables client-side pagination. Initial rows per page (clamped to one of
   * 10, 20, 50, 100). The footer lets the user switch among those sizes or show all rows.
   * 0 or undefined = show all rows, no pagination UI.
   */
  pageSize?: number;
  /** Label for the item name in pagination footer (e.g. "itens", "categorias"). */
  itemLabel?: string;
  /** Current sort column key (when using sortable columns). */
  sortBy?: string;
  /** Current sort direction. */
  sortDirection?: "asc" | "desc";
  /** Called when a sortable column header is clicked. */
  onSort?: (columnKey: string) => void;
  /** When true, shows a checkbox column for multi-select and a "select all" in the header. */
  selectable?: boolean;
  /** Controlled selected row keys (from keyExtractor). When provided with onSelectionChange, selection is controlled. */
  selectedKeys?: (string | number)[];
  /** Called when selection changes. Receives selected keys and selected items. */
  onSelectionChange?: (selectedKeys: (string | number)[], selectedItems: T[]) => void;
  /** Shown below the table header when there is a selection (desktop + mobile). */
  renderSelectionToolbar?: (info: { selectedCount: number }) => React.ReactNode;
}

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;

function normalizePageSize(prop: number): (typeof PAGE_SIZE_OPTIONS)[number] {
  if ((PAGE_SIZE_OPTIONS as readonly number[]).includes(prop)) {
    return prop as (typeof PAGE_SIZE_OPTIONS)[number];
  }
  return 10;
}

function defaultGetSearchableText<T>(item: T, columns: ListColumn<T>[]): string {
  return columns
    .map((col) => {
      const val = item[col.key as keyof T];
      return val != null ? String(val) : "";
    })
    .join(" ")
    .toLowerCase();
}

export function DataList<T>({
  data,
  columns,
  actions,
  keyExtractor,
  containerClassName = "bg-white dark:bg-[#2D3748] rounded-lg sm:rounded-xl shadow-sm border border-gray-200 dark:border-[#374151] overflow-hidden",
  headerClassName = "bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-[#1A202C] dark:to-[#2D3748] px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-[#374151]",
  rowClassName = "px-4 sm:px-6 py-3 sm:py-4 hover:bg-gray-50 dark:hover:bg-[#374151] transition-colors duration-150",
  emptyMessage = "Nenhum item encontrado",
  loading = false,
  onRowClick,
  showFilter = false,
  filterPlaceholder = "Filtrar...",
  getSearchableText,
  pageSize = 0,
  itemLabel = "itens",
  sortBy,
  sortDirection,
  onSort,
  selectable = false,
  selectedKeys: controlledSelectedKeys,
  onSelectionChange,
  renderSelectionToolbar,
}: DataListProps<T>) {
  const isPaginated = pageSize > 0;
  const [effectivePageSize, setEffectivePageSize] = useState(() =>
    isPaginated ? normalizePageSize(pageSize) : 10
  );

  useEffect(() => {
    if (pageSize > 0) {
      setEffectivePageSize(normalizePageSize(pageSize));
    }
  }, [pageSize]);

  const [filterTerm, setFilterTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [internalSelected, setInternalSelected] = useState<Set<string | number>>(new Set());
  const isControlled = controlledSelectedKeys != null;
  const selectedSet = isControlled
    ? new Set(controlledSelectedKeys)
    : internalSelected;

  const setSelected = (next: Set<string | number>) => {
    if (!isControlled) setInternalSelected(next);
    const items = data.filter((item) => next.has(keyExtractor(item) as string | number));
    onSelectionChange?.(Array.from(next), items);
  };

  const toggleRow = (item: T) => {
    const key = keyExtractor(item) as string | number;
    const next = new Set(selectedSet);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setSelected(next);
  };

  const filteredData = useMemo(() => {
    if (!showFilter || !filterTerm.trim()) return data;
    const term = filterTerm.trim().toLowerCase();
    const getText = getSearchableText
      ? (item: T) => getSearchableText(item).toLowerCase()
      : (item: T) => defaultGetSearchableText(item, columns);
    return data.filter((item) => getText(item).includes(term));
  }, [data, filterTerm, showFilter, getSearchableText, columns]);

  const rowsPerPage = isPaginated && effectivePageSize > 0 ? effectivePageSize : 0;
  const totalPages = rowsPerPage > 0 ? Math.max(1, Math.ceil(filteredData.length / rowsPerPage)) : 1;
  const safePage = Math.min(Math.max(1, currentPage), totalPages);
  const paginatedData =
    rowsPerPage > 0
      ? filteredData.slice((safePage - 1) * rowsPerPage, safePage * rowsPerPage)
      : filteredData;

  const toggleSelectAll = () => {
    const pageKeys = new Set(paginatedData.map((item) => keyExtractor(item) as string | number));
    const allSelected = pageKeys.size > 0 && Array.from(pageKeys).every((k) => selectedSet.has(k));
    const next = new Set(selectedSet);
    if (allSelected) pageKeys.forEach((k) => next.delete(k));
    else pageKeys.forEach((k) => next.add(k));
    setSelected(next);
  };

  const selectAllCheckboxRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const el = selectAllCheckboxRef.current;
    if (!el) return;
    const someSelected =
      paginatedData.length > 0 &&
      paginatedData.some((item) => selectedSet.has(keyExtractor(item) as string | number));
    const allSelected =
      paginatedData.length > 0 &&
      paginatedData.every((item) => selectedSet.has(keyExtractor(item) as string | number));
    el.indeterminate = someSelected && !allSelected;
  }, [selectedSet, paginatedData]);

  // Reset to page 1 when filter, data length, or page size changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filterTerm, data.length, effectivePageSize]);

  const getGridTemplateColumns = () => {
    // Minimum width per column so they don't shrink on resize; table becomes scrollable instead
    const minColPx = 72;
    const checkboxCol = selectable ? "auto " : "";

    // Check if any columns have custom widths
    const hasCustomWidths = columns.some((col) => col.width);

    if (hasCustomWidths) {
      // Use minmax(min, width) so columns keep at least minColPx and don't squash
      const columnWidths = columns
        .map((col) => `minmax(${minColPx}px, ${col.width || "1fr"})`)
        .join(" ");
      const actionsWidth = actions && actions.length > 0 ? " 120px" : "";
      return checkboxCol + columnWidths + actionsWidth;
    }

    // Default: equal width columns with minimum
    return checkboxCol + `repeat(${columns.length + (actions ? 1 : 0)}, minmax(${minColPx}px, 1fr))`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-[#63B3ED]"></div>
        <span className="ml-2 text-gray-600 dark:text-slate-400">Carregando...</span>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={containerClassName}>
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-slate-400">{emptyMessage}</p>
        </div>
      </div>
    );
  }

  const startItem = rowsPerPage > 0 ? (safePage - 1) * rowsPerPage + 1 : 1;
  const endItem =
    rowsPerPage > 0 ? Math.min(safePage * rowsPerPage, filteredData.length) : filteredData.length;
  const showPaginationFooter = isPaginated && filteredData.length > 0;
  const hasMultiplePages = totalPages > 1;

  return (
    <div className={containerClassName}>
      {/* Filter */}
      {showFilter && (
        <div className="p-3 sm:p-4 border-b border-gray-200 dark:border-[#374151] bg-gray-50/50 dark:bg-[#1A202C]/50">
          <div className="relative max-w-xs">
            <span className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400 dark:text-slate-500">
              🔍
            </span>
            <input
              type="text"
              placeholder={filterPlaceholder}
              value={filterTerm}
              onChange={(e) => setFilterTerm(e.target.value)}
              className="block w-full pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-[#374151] dark:bg-[#2D3748] dark:text-slate-200 dark:placeholder-slate-500 rounded-lg focus:ring-2 focus:ring-sky-500 dark:focus:ring-[#63B3ED] focus:border-sky-500 dark:focus:border-[#63B3ED]"
            />
          </div>
        </div>
      )}

      {/* Scrollable table area: header + rows */}
      <div className="overflow-x-auto">
        <div className="min-w-max">
      {/* Desktop Header - Hidden on mobile */}
      <div className={`${headerClassName} hidden md:block`}>
        <div
          className="grid gap-4"
          style={{
            gridTemplateColumns: getGridTemplateColumns(),
          }}
        >
          {selectable && (
            <div className="flex items-center">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  ref={selectAllCheckboxRef}
                  type="checkbox"
                  checked={
                    paginatedData.length > 0 &&
                    paginatedData.every((item) => selectedSet.has(keyExtractor(item) as string | number))
                  }
                  onChange={toggleSelectAll}
                  className="h-4 w-4 rounded border border-slate-500 bg-[#374151] text-[#63B3ED] focus:ring-2 focus:ring-[#63B3ED] cursor-pointer"
                  aria-label="Selecionar todos"
                />
              </label>
            </div>
          )}
          {columns.map((column, index) => {
            const key = String(column.key);
            const isSortable = column.sortable && onSort;
            const isActive = sortBy === key;
            const content = (
              <>
                {column.label}
                {isSortable && isActive && (
                  <span className="ml-1 inline-block" aria-hidden>
                    {sortDirection === "asc" ? " ↑" : " ↓"}
                  </span>
                )}
              </>
            );
            return (
              <div
                key={index}
                className={`text-xs sm:text-sm font-semibold text-gray-600 dark:text-slate-300 tracking-wide ${
                  column.className || ""
                } ${isSortable ? "flex items-center" : ""}`}
              >
                {isSortable ? (
                  <button
                    type="button"
                    onClick={() => onSort(key)}
                    className="text-left hover:text-gray-900 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-sky-500 dark:focus:ring-[#63B3ED] focus:ring-offset-1 rounded px-1 -mx-1"
                  >
                    {content}
                  </button>
                ) : (
                  content
                )}
              </div>
            );
          })}
          {actions && actions.length > 0 && (
            <div className="flex items-center justify-center text-xs sm:text-sm font-semibold text-gray-600 dark:text-slate-300 tracking-wide">
              Ações
            </div>
          )}
        </div>
      </div>

      {selectable &&
        selectedSet.size > 0 &&
        renderSelectionToolbar && (
          <>
            <div className="hidden md:block border-b border-gray-200 dark:border-[#374151] bg-white dark:bg-[#2D3748] px-4 sm:px-6 py-2.5">
              {renderSelectionToolbar({ selectedCount: selectedSet.size })}
            </div>
            <div className="md:hidden border-b border-gray-200 dark:border-[#374151] bg-white dark:bg-[#2D3748] px-4 py-2.5">
              {renderSelectionToolbar({ selectedCount: selectedSet.size })}
            </div>
          </>
        )}

      {/* Content */}
      {filteredData.length === 0 ? (
        <div className="text-center py-12 px-4">
          <p className="text-gray-500 dark:text-slate-400">
            {showFilter && filterTerm.trim()
              ? "Nenhum resultado para o filtro informado."
              : emptyMessage}
          </p>
        </div>
      ) : (
      <div className="divide-y divide-gray-100 dark:divide-[#374151]">
        {paginatedData.map((item) => {
          const dynamicRowClassName = typeof rowClassName === 'function' 
            ? rowClassName(item) 
            : rowClassName;
          return (
          <div
            key={keyExtractor(item)}
            className={`${
              onRowClick ? "cursor-pointer" : ""
            }`}
          >
            {/* Desktop View - Grid Layout */}
            <div
              className={`hidden md:grid gap-4 ${dynamicRowClassName}`}
              style={{
                gridTemplateColumns: getGridTemplateColumns(),
              }}
              onClick={() => onRowClick?.(item)}
            >
              {selectable && (
                <div className="flex items-center" onClick={(e) => e.stopPropagation()}>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={selectedSet.has(keyExtractor(item) as string | number)}
                      onChange={() => toggleRow(item)}
                      className="h-4 w-4 rounded border border-slate-500 bg-[#374151] text-[#63B3ED] focus:ring-2 focus:ring-[#63B3ED] cursor-pointer"
                      aria-label={`Selecionar ${keyExtractor(item)}`}
                    />
                  </label>
                </div>
              )}
              {columns.map((column, columnIndex) => (
                <div
                  key={columnIndex}
                  className={`flex items-center ${column.className || ""}`}
                >
                  {column.render
                    ? column.render(item)
                    : String(item[column.key as keyof T] || "")}
                </div>
              ))}

              {/* Actions */}
              {actions && actions.length > 0 && (
                <div className="flex items-center justify-center w-full space-x-2">
                  {actions
                    .filter((action) => action.visible?.(item) !== false)
                    .map((action, actionIndex) => (
                      <button
                        key={actionIndex}
                        onClick={(e) => {
                          e.stopPropagation();
                          action.onClick(item);
                        }}
                        disabled={action.disabled?.(item)}
                        className={`p-2 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                          action.className ||
                          "text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#374151]"
                        }`}
                        title={action.title || action.label}
                      >
                        {action.icon}
                      </button>
                    ))}
                </div>
              )}
            </div>

            {/* Mobile View - Card Layout */}
            <div
              className={`md:hidden ${dynamicRowClassName} p-4 sm:p-6`}
              onClick={() => onRowClick?.(item)}
            >
              <div className="space-y-3">
                {selectable && (
                  <div className="flex items-center pb-2 border-b border-gray-100 dark:border-[#374151] mb-2" onClick={(e) => e.stopPropagation()}>
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={selectedSet.has(keyExtractor(item) as string | number)}
                        onChange={() => toggleRow(item)}
                        className="h-4 w-4 rounded border border-slate-500 bg-[#374151] text-[#63B3ED] focus:ring-2 focus:ring-[#63B3ED] cursor-pointer"
                        aria-label={`Selecionar ${keyExtractor(item)}`}
                      />
                      <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide">Selecionar</span>
                    </label>
                  </div>
                )}
                {columns.map((column, columnIndex) => (
                  <div
                    key={columnIndex}
                    className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 ${
                      column.className || ""
                    }`}
                  >
                    <span className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide sm:w-24 flex-shrink-0">
                      {column.label}:
                    </span>
                    <div className="flex-1 text-sm sm:text-base text-gray-900 dark:text-slate-200 break-words">
                      {column.render
                        ? column.render(item)
                        : String(item[column.key as keyof T] || "")}
                    </div>
                  </div>
                ))}

                {/* Actions - Mobile */}
                {actions && actions.length > 0 && (
                  <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-200 dark:border-[#374151] mt-3">
                    {actions
                      .filter((action) => action.visible?.(item) !== false)
                      .map((action, actionIndex) => (
                        <button
                          key={actionIndex}
                          onClick={(e) => {
                            e.stopPropagation();
                            action.onClick(item);
                          }}
                          disabled={action.disabled?.(item)}
                          className={`p-2 sm:p-2.5 rounded-lg transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-base sm:text-lg ${
                            action.className ||
                            "text-gray-600 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-[#374151]"
                          }`}
                          title={action.title || action.label}
                        >
                          {action.icon}
                        </button>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          );
        })}
      </div>
      )}
        </div>
      </div>

      {/* Pagination footer */}
      {showPaginationFooter && (
        <div className="flex flex-col gap-3 px-4 sm:px-6 py-3 border-t border-gray-200 dark:border-[#374151] bg-gray-50/50 dark:bg-[#1A202C]/50 text-sm text-gray-600 dark:text-slate-400">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <span>
              {startItem}–{endItem} de {filteredData.length} {itemLabel}
            </span>
            {hasMultiplePages && (
              <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-end">
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-[#374151] bg-white dark:bg-[#2D3748] dark:text-slate-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-[#374151]"
                >
                  Anterior
                </button>
                <span className="px-2">
                  Página {safePage} de {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                  className="px-3 py-1.5 rounded-lg border border-gray-300 dark:border-[#374151] bg-white dark:bg-[#2D3748] dark:text-slate-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-[#374151]"
                >
                  Próxima
                </button>
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-gray-200/80 dark:border-[#374151]/80 sm:border-t-0 sm:pt-0">
            <span className="text-xs font-medium text-gray-500 dark:text-slate-500 shrink-0">
              Itens por página:
            </span>
            <div className="flex items-center gap-1" role="group" aria-label="Itens por página">
              {PAGE_SIZE_OPTIONS.map((n) => (
                <button
                  key={n}
                  type="button"
                  onClick={() => setEffectivePageSize(n)}
                  className={`min-w-[2.25rem] px-2 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                    effectivePageSize === n
                      ? "border-sky-600 bg-sky-50 text-sky-800 dark:border-[#63B3ED] dark:bg-sky-900/40 dark:text-sky-200"
                      : "border-gray-300 dark:border-[#374151] bg-white dark:bg-[#2D3748] text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-[#374151]"
                  }`}
                  aria-pressed={effectivePageSize === n}
                >
                  {n}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setEffectivePageSize(0)}
                className={`px-2 py-1.5 rounded-lg text-xs font-semibold border transition-colors ${
                  effectivePageSize === 0
                    ? "border-sky-600 bg-sky-50 text-sky-800 dark:border-[#63B3ED] dark:bg-sky-900/40 dark:text-sky-200"
                    : "border-gray-300 dark:border-[#374151] bg-white dark:bg-[#2D3748] text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-[#374151]"
                }`}
                aria-pressed={effectivePageSize === 0}
              >
                Todos
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default DataList;
